/**
 * Desplegables que consultan al servidor
 * -----------------------------------------------------------------------------
 * Un `<select>` mete en el HTML una etiqueta `<option>` por fila. Con un catálogo
 * de miles de registros eso es una respuesta enorme, un parseo lento y memoria
 * ocupada para mostrar cinco líneas. Y el buscador del desplegable filtra sobre
 * lo que ya viajó, así que no sirve de nada haberlo traído todo.
 *
 * Aquí el select pide páginas al servidor: al abrirse trae la primera, al llegar
 * al final de la lista trae la siguiente, y al escribir manda el texto al
 * endpoint en vez de filtrar las opciones locales.
 *
 *   <select id="proveedor" name="proveedor_id" class="form-select"
 *           data-mdb-select-init
 *           data-company-remote-url="/proveedores/buscar"
 *           data-company-remote-min-chars="2">
 *     <option value="8452">Insumos del Valle</option>  <!-- el ya elegido -->
 *   </select>
 *
 * El endpoint recibe `?q=&page=&limit=` y responde:
 *
 *   { "results": [ { "value": 8452, "label": "Insumos del Valle" } ],
 *     "hasMore": true }
 *
 * Un desplegable puede depender de otros campos del mismo formulario, y de
 * **más de uno**: el lote que se despacha depende de la bodega y de la
 * referencia a la vez, porque el mismo lote puede estar en dos bodegas.
 *
 *   data-company-remote-depends-on="bodega,referencia"
 *
 * Cada padre viaja con el nombre de su campo —`?bodega=…&referencia=…`— salvo
 * que `data-company-remote-parent-param` diga otros, en el mismo orden.
 *
 * **`hasMore`, no `total`**: contar filas obliga a un `COUNT(*)` sobre la tabla
 * entera en cada scroll. Basta con pedir una fila de más de las que caben en la
 * página: si llega, es que hay más.
 *
 * No se parchea MDB. Se usa `search.mdb.select`, que es cancelable, para desviar
 * la búsqueda; y el MutationObserver que MDB ya trae para repintar cuando se
 * añaden opciones. Así una actualización de MDB no rompe nada.
 */

import { selectES } from './locale-es.js';

const ATTR = 'data-company-remote-url';

/** Distancia al final de la lista, en píxeles, a la que se pide la siguiente página. */
const SCROLL_MARGIN = 48;

/** `"bodega, referencia"` → `['bodega', 'referencia']`. Vacío si no hay nada. */
function splitNames(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name !== '');
}

function number(value, fallback) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

class RemoteSelect {
  constructor(element, select) {
    this.element = element; // el <select> nativo
    this.select = select; // la instancia de MDB
    const data = element.dataset;

    this.url = element.getAttribute(ATTR);
    this.pageSize = number(data.companyRemotePageSize, 50);
    this.minChars = number(data.companyRemoteMinChars, 0);
    this.valueField = data.companyRemoteValue || 'value';
    this.labelField = data.companyRemoteLabel || 'label';
    // Campo del JSON que marca la opción como no elegible, y el que trae el
    // motivo para enseñarlo debajo del nombre.
    this.disabledField = data.companyRemoteDisabled || 'disabled';
    this.metaField = data.companyRemoteMeta || 'meta';
    this.params = {
      query: data.companyRemoteParamQuery || 'q',
      page: data.companyRemoteParamPage || 'page',
      limit: data.companyRemoteParamLimit || 'limit',
    };
    this.debounce = number(data.companyRemoteDebounce, 300);

    // Catálogo encadenado: las ciudades dependen del departamento, y este del
    // país. El nombre del campo padre viaja como parámetro en la consulta.
    // En plural desde 1.15: `depends-on="bodega,referencia"`. Con un solo
    // nombre se comporta igual que antes, que es la mayoría de los casos.
    this.dependsOn = splitNames(data.companyRemoteDependsOn);
    this.parentParams = splitNames(data.companyRemoteParentParam);

    this.page = 0;
    this.query = '';
    this.hasMore = true;
    this.loading = false;
    this.loadedOnce = false;
    this.controller = null;
    this.timer = null;

    this._onSearch = this._onSearch.bind(this);
    this._onOpened = this._onOpened.bind(this);
    this._onClosed = this._onClosed.bind(this);
    this._onScroll = this._onScroll.bind(this);

    // Nombres de evento completos: MDB los lanza como `CustomEvent` con el
    // nombre y el espacio de nombres juntos.
    element.addEventListener('search.mdb.select', this._onSearch);
    element.addEventListener('opened.mdb.select', this._onOpened);
    element.addEventListener('closed.mdb.select', this._onClosed);

    this._dropSelectedAttribute();
    this._watchParent();

    element.__companyRemoteSelect = this;
  }

  /**
   * Los campos de los que depende, buscados en el formulario que los contiene.
   *
   * Cada uno con el nombre del parámetro que le toca: el de
   * `parent-param` en su misma posición, o el del propio campo.
   */
  get parents() {
    const scope = this.element.form || document;

    return this.dependsOn
      .map((name, i) => ({
        param: this.parentParams[i] || name,
        field: scope.querySelector(`[name="${name}"]`),
      }))
      .filter((parent) => parent.field);
  }

  /**
   * Lo elegido, sea uno o varios.
   *
   * `element.value` en un `multiple` devuelve **solo el primero**, así que al
   * repintar la lista se perdían todos los demás sin decir nada: se elegían
   * tres impuestos, se buscaba otra cosa, y quedaba uno.
   *
   * Siempre un array, también con uno solo: dos formas de devolver lo mismo son
   * dos ramas en cada sitio que lo lea.
   *
   * @returns {string[]}
   */
  _elegido() {
    return Array.prototype.filter
      .call(this.element.options, (option) => option.selected && option.value !== '')
      .map((option) => option.value);
  }

  /**
   * @param {string[]} valores
   */
  _devolverElegido(valores) {
    if (!valores.length) return;

    if (!this.element.multiple) {
      this.element.value = valores[0];

      return;
    }

    // Uno a uno y no por `value`: en un `multiple`, asignar `value` marca uno y
    // desmarca el resto, que es justo lo que se venía a evitar.
    const marcados = new Set(valores);

    Array.prototype.forEach.call(this.element.options, (option) => {
      if (marcados.has(option.value)) option.selected = true;
    });
  }

  /**
   * Al cambiar un padre, lo elegido aquí deja de tener sentido: una ciudad de
   * otro departamento. Se vacía y se vuelve a consultar en cuanto se abra.
   */
  _watchParent() {
    this.parents.forEach(({ field }) => {
      field.addEventListener('change', () => {
        this.element.value = '';
        // Sin la opción vacía cuando admite varios: ahí «ninguno» se dice no
        // marcando nada, y una opción en blanco en la lista solo es una línea
        // que se puede pulsar y no hace nada.
        this.element.innerHTML = this.element.multiple ? '' : '<option value=""></option>';

        this.loadedOnce = false;
        this.page = 0;
        this.hasMore = true;

        // Repinta el desplegable con la lista ya vacía.
        const select = this.select;
        if (select && select._updateSelections) select._updateSelections();
      });
    });
  }

  /**
   * Pasa la elección inicial del atributo a la propiedad.
   *
   * MDB decide qué opción está elegida con
   * `nativeOption.selected || nativeOption.hasAttribute('selected')`. El
   * `<option selected>` que pinta la plantilla deja ese atributo puesto para
   * siempre: el usuario elige otra, pero en el siguiente repintado la vieja
   * vuelve a marcarse. La propiedad sí refleja la elección viva.
   */
  _dropSelectedAttribute() {
    const index = this.element.selectedIndex;
    let touched = false;

    Array.prototype.forEach.call(this.element.options, (option) => {
      if (!option.hasAttribute('selected')) return;
      option.removeAttribute('selected');
      touched = true;
    });

    if (touched) this.element.selectedIndex = index;
  }

  // ---------------------------------------------------------------------------
  // Eventos del desplegable
  // ---------------------------------------------------------------------------

  _onSearch(event) {
    // Se corta el filtrado local: quien busca es el servidor. MDB comprueba
    // `defaultPrevented` justo después de lanzarlo.
    event.preventDefault();

    const term = (event.value || '').trim();
    if (this.timer) clearTimeout(this.timer);

    if (term.length > 0 && term.length < this.minChars) {
      this._status(selectES.minChars.replace('{n}', String(this.minChars)));
      return;
    }

    this.timer = setTimeout(() => this.load(term, 1), this.debounce);
  }

  _onOpened() {
    const wrapper = this.select.optionsWrapper;
    if (wrapper) wrapper.addEventListener('scroll', this._onScroll);

    // La primera página se pide al abrir, no al cargar la página: un formulario
    // con doce selectores no debe disparar doce consultas que quizá nadie mire.
    if (!this.loadedOnce && this.minChars === 0) this.load('', 1);
    else if (!this.loadedOnce) this._status(selectES.minChars.replace('{n}', String(this.minChars)));
  }

  _onClosed() {
    const wrapper = this.select.optionsWrapper;
    if (wrapper) wrapper.removeEventListener('scroll', this._onScroll);
  }

  _onScroll() {
    const wrapper = this.select.optionsWrapper;
    if (!wrapper || this.loading || !this.hasMore) return;

    if (wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - SCROLL_MARGIN) {
      this.load(this.query, this.page + 1);
    }
  }

  // ---------------------------------------------------------------------------
  // Consulta
  // ---------------------------------------------------------------------------

  /**
   * @param {string} query Texto escrito, ya recortado.
   * @param {number} page Página a pedir, empezando en 1.
   */
  load(query, page) {
    // Se cancela lo que estuviera en vuelo en vez de rechazar lo nuevo. Al revés
    // —descartar la petición nueva mientras hay una en curso— la búsqueda que
    // acaba de escribir el usuario se pierde en silencio.
    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;

    this.loading = true;
    this.query = query;
    this._status(selectES.loading);

    const url = new URL(this.url, window.location.href);
    url.searchParams.set(this.params.query, query);
    url.searchParams.set(this.params.page, String(page));
    url.searchParams.set(this.params.limit, String(this.pageSize));

    // Los valores de los campos padre. Sin ellos, el endpoint devolvería las
    // ciudades de todo el país en vez de las del departamento elegido.
    this.parents.forEach(({ param, field }) => {
      url.searchParams.set(param, field.value || '');
    });

    fetch(url.toString(), {
      signal: this.controller ? this.controller.signal : undefined,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const rows = this._rowsOf(json);

        this.hasMore = this._hasMoreOf(json, rows, page);
        this._render(rows, page);

        this.page = page;
        this.loadedOnce = true;
        this.loading = false;
        this._status(null);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return; // la reemplazó otra consulta
        this.loading = false;
        // Se avisa. Un fallo de red que deja la lista vacía se lee como «no hay
        // datos», y el usuario da por hecho que no existe lo que busca.
        this._status(selectES.error, 'error');
      });
  }

  /**
   * La segunda línea de una opción, en gris y debajo del nombre.
   *
   * Para lo que acompaña sin ser el nombre: el motivo por el que un proveedor
   * no se puede elegir, o de qué producto es esta referencia. Escribirlo dentro
   * de la etiqueta -«Filete de mora (Filetes)»- lo mete en las búsquedas y se lo
   * lleva pegado quien copie el nombre.
   *
   * Se quita cuando deja de venir: una opción que se refresca y ya no trae la
   * segunda línea no puede quedarse con la de antes.
   */
  _segundaLinea(option, row) {
    const meta = row[this.metaField];

    if (meta) {
      option.setAttribute('data-mdb-secondary-text', String(meta));
    } else {
      option.removeAttribute('data-mdb-secondary-text');
    }
  }
  /** Acepta `{results}`, `{data}` o un array pelado. */
  _rowsOf(json) {
    if (Array.isArray(json)) return json;
    if (json && Array.isArray(json.results)) return json.results;
    if (json && Array.isArray(json.data)) return json.data;
    return [];
  }

  /**
   * Si hay más páginas. Lo dice el servidor, que es quien puede saberlo barato:
   * pide una fila de más de las que va a devolver y mira si llegó.
   *
   * Sin `hasMore` se deduce de que la página venga llena. Es una suposición
   * razonable, con un caso feo: si el total es múltiplo exacto del tamaño de
   * página, se pide una página de más que vuelve vacía. Por eso conviene
   * mandarlo.
   */
  _hasMoreOf(json, rows, page) {
    if (json && typeof json.hasMore === 'boolean') return json.hasMore;
    if (json && typeof json.has_more === 'boolean') return json.has_more;
    if (json && typeof json.total === 'number') return page * this.pageSize < json.total;
    return rows.length >= this.pageSize;
  }

  _render(rows, page) {
    const wrapper = this.select.optionsWrapper;
    const scroll = wrapper ? wrapper.scrollTop : 0;

    // ANTES de tocar el `<select>`, y este orden es el que importa.
    //
    // Al abrirse, MDB llama a `_filterOptions` y guarda en `_filteredOptionsList`
    // las opciones de ESE momento —las dos que traía el HTML—. Esa lista es la
    // que devuelve el getter `options`, y de ella se alimentan dos cosas: el
    // manejador del clic, que busca ahí el id del elemento pulsado, y
    // `_setDefaultSelections()`, que reconstruye el modelo al repintar.
    //
    // Si sigue viva cuando llegan las filas del servidor, el modelo se rellena
    // con los objetos VIEJOS. Entonces elegir una opción deselecciona un objeto
    // huérfano en vez del que se está viendo: el desplegable queda con dos
    // marcadas y hay que insistir para que tome el valor nuevo.
    //
    // Anulándola aquí, `_setDefaultSelections()` lee `_plainOptions`, que sí se
    // regenera en el repintado. Si hay texto en el buscador, `_updateSelections`
    // pasa por `_filterOptions` y la vuelve a dejar coherente.
    this.select._filteredOptionsList = null;

    const selectedValue = this._elegido();

    if (page === 1) {
      // Consulta nueva: se cambia la lista entera. Se conservan la opción vacía
      // y la que estuviera elegida, para no perder el valor del formulario al
      // buscar otra cosa.
      const keep = [];
      Array.prototype.forEach.call(this.element.options, (option) => {
        if (option.value === '' || option.selected) keep.push(option);
      });

      this.element.innerHTML = '';
      keep.forEach((option) => this.element.appendChild(option));
    }

    // Por valor y no un `Set` de valores: cuando una fila del servidor habla de
    // una opción que ya está, hay que poder **tocarla**.
    const existing = new Map();
    Array.prototype.forEach.call(this.element.options, (option) => {
      if (!existing.has(option.value)) existing.set(option.value, option);
    });

    rows.forEach((row) => {
      const value = String(row[this.valueField]);
      if (value === 'undefined') return;

      const label = String(row[this.labelField]);
      const yaEsta = existing.get(value);

      // **La que ya estaba se refresca, no se ignora.**
      //
      // La plantilla pinta a mano la opción del valor guardado para no perderlo
      // mientras llega el catálogo, y ahí escribe el nombre que tenga - que a
      // veces no tiene ninguno y acaba siendo el identificador. Saltarse la fila
      // del servidor dejaba ese texto de relleno para siempre: el desplegable
      // enseñando `fd201ba2-815a-...` con el nombre real ya recibido y tirado a
      // la basura, en un campo que el usuario ni siquiera había tocado.
      //
      // El servidor es quien sabe cómo se llama. Si contesta por ese valor, su
      // nombre gana. La elección no se toca: se cambia lo que se lee, no lo que
      // vale.
      if (yaEsta) {
        if (label !== 'undefined' && yaEsta.textContent !== label) {
          yaEsta.textContent = label;
        }

        // Y su segunda línea, por lo mismo. La plantilla pinta la opción del
        // valor guardado sin ella -no la tiene de dónde sacar-, así que sin
        // esto la única opción ya elegida se queda a una línea mientras todas
        // las demás salen a dos, que se lee como un fallo de pintado.
        this._segundaLinea(yaEsta, row);

        return;
      }

      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;

      // Opción que se ve pero no se puede elegir: un proveedor bloqueado, una
      // sede cerrada. Se muestra en vez de esconderla porque desaparecer sin
      // explicación hace pensar que se perdió el registro.
      //
      // MDB lo resuelve con el atributo: lo lee en `_createOptionObject` y su
      // manejador de clic comprueba `!option.disabled`.
      if (row[this.disabledField]) option.disabled = true;

      this._segundaLinea(option, row);

      this.element.appendChild(option);
      existing.set(value, option);
    });

    // Vaciar el `<select>` pierde la elección: se devuelve por valor, no por
    // atributo, para que MDB la lea de la propiedad.
    this._devolverElegido(selectedValue);

    // Al repintar, la lista vuelve arriba. MDB repinta desde su observador, que
    // corre como microtarea encolada al mutar; una microtarea encolada AQUÍ va
    // justo detrás, con el repintado ya hecho.
    //
    // No sirve `requestAnimationFrame`: no corre en pestañas de segundo plano
    // ni donde no se componen fotogramas, y la lista se quedaría saltando al
    // principio en cada página.
    if (page > 1) {
      Promise.resolve().then(() => {
        const current = this.select.optionsWrapper;
        if (current) current.scrollTop = scroll;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Aviso al pie de la lista
  // ---------------------------------------------------------------------------

  /**
   * El aviso va FUERA del contenedor con scroll: MDB reemplaza la lista de
   * opciones al repintar, y ahí dentro se lo llevaría por delante.
   *
   * @param {string|null} text Texto, o `null` para quitarlo.
   * @param {string} [kind] `error` para pintarlo como fallo.
   */
  _status(text, kind) {
    const dropdown = this.select.dropdown;
    if (!dropdown) return;

    let row = dropdown.querySelector('.select-remote-status');

    if (!text) {
      if (row) row.remove();
      return;
    }

    if (!row) {
      row = document.createElement('div');
      row.className = 'select-remote-status';
      dropdown.appendChild(row);
    }

    row.classList.toggle('select-remote-error', kind === 'error');
    row.textContent = text;
  }

  dispose() {
    if (this.controller) this.controller.abort();
    if (this.timer) clearTimeout(this.timer);

    this.element.removeEventListener('search.mdb.select', this._onSearch);
    this.element.removeEventListener('opened.mdb.select', this._onOpened);
    this.element.removeEventListener('closed.mdb.select', this._onClosed);
    this._onClosed();

    delete this.element.__companyRemoteSelect;
  }
}

/**
 * Monta los desplegables remotos que haya en `root`.
 *
 * Va DESPUÉS de que existan las instancias de Select: este componente se apoya
 * en ellas para leer el desplegable ya pintado.
 *
 * @param {ParentNode} [root=document]
 * @param {Object} components Componentes de MDB.
 */
export function setupRemoteSelects(root = document, components = {}) {
  const Select = components.Select;
  if (!Select || !root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll(`select[${ATTR}]`).forEach((element) => {
    if (element.__companyRemoteSelect) return;

    const select = Select.getInstance(element);
    if (select) new RemoteSelect(element, select);
  });
}

export { RemoteSelect };
